import { LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { PageBody } from "@uhg-abyss/web/ui/PageBody";
import { Router } from "@uhg-abyss/web/ui/Router";
import type { ComponentProps, ComponentType, ReactNode } from "react";

const CommonUiRoot = styled("div", {
  zIndex: 3,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const CommonUiTopSection = styled("div", {
  width: "100%",
});

const CommonUiBodyText = styled("div", {
  color: "$app.color.text.primary",
  textAlign: "center",
  paddingBottom: "32px",
});

const CommonUiPageBody = styled(PageBody, {
  width: "100%",
  "abyss-page-body-container": {
    padding: "0",
  },
});

const CommonUiFormContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
});

interface CommonUIBaseProps {
  FormComponent: ComponentType;
  metaTitle?: string;
  formMaxWidth?: string;
  topSectionMobileMaxWidth?: string;
  topSectionMobileCss?: ComponentProps<typeof CommonUiTopSection>["css"];
}

type CommonUIProps = CommonUIBaseProps &
  (
    | {
        introContent: ReactNode;
        headingText?: never;
        bodyText?: never;
      }
    | {
        introContent?: never;
        headingText: ReactNode;
        bodyText: ReactNode;
      }
  );

export const CommonUI = ({
  FormComponent,
  headingText,
  bodyText,
  introContent,
  metaTitle = "",
  formMaxWidth,
  topSectionMobileMaxWidth,
  topSectionMobileCss,
}: CommonUIProps) => {
  const isSmall = useMediaQuery(MEDIA_QUERIES.mobileMax);

  return (
    <CommonUiRoot
      data-testid="test-id-common-ui"
      css={{
        width: "100%",
        minHeight: "300px",
        paddingTop: isSmall ? "0" : "42px",
      }}
    >
      <Router.MetaTags title={metaTitle} />
      <CommonUiTopSection
        data-testid="test-id-common-ui-top-section"
        css={{
          maxWidth: isSmall ? (topSectionMobileMaxWidth ?? "350px") : LAYOUT_CONSTANTS.maxFormWidth,
          ...(isSmall ? topSectionMobileCss : {}),
        }}
      >
        {introContent ?? (
          <>
            <Heading
              data-testid="test-id-common-ui-heading"
              textAlign="center"
              css={{
                fontSize: isSmall ? "28px" : "36px",
                lineHeight: isSmall ? "36px" : "48px",
                marginBottom: "8px",
                padding: "0 16px",
              }}
            >
              <div data-testid="test-id-common-ui-heading-text">{headingText}</div>
            </Heading>
            <CommonUiPageBody data-testid="test-id-common-ui-page-body">
              <CommonUiBodyText
                data-testid="test-id-common-ui-body-text"
                css={{
                  fontSize: isSmall ? "16px" : "18px",
                }}
              >
                {bodyText}
              </CommonUiBodyText>
            </CommonUiPageBody>
          </>
        )}
      </CommonUiTopSection>
      {
        <CommonUiFormContainer
          data-testid="test-id-common-ui-form-container"
          css={{
            width: "100%",
            maxWidth: formMaxWidth ?? LAYOUT_CONSTANTS.maxFormWidth,
          }}
        >
          <FormComponent />
        </CommonUiFormContainer>
      }
    </CommonUiRoot>
  );
};
